import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    output,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { SideListModel } from '@fi-app/utilities/side-list/side-list.model';
import { FiSideListType } from '@fi-app/utilities/side-list/side-list-type';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { AppTextService } from '@fi-shared/app-text';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import {
    IContentLanguage,
    IContentLanguageLabelModel,
} from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { debounce, takeUntil } from 'rxjs/operators';
import { interval, Subject } from 'rxjs';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { createMap } from '@fi-shared/utils/create-map';
import { isDefined } from '@fi-shared/utils';

@Component({
    selector: 'fi-edit-translation',
    templateUrl: './edit-translation.component.html',
    styleUrls: ['./edit-translation.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-edit-translation' },
    providers: [{ provide: FiComponentsContainer, useExisting: EditTranslationComponent }],
    standalone: false,
})
export class EditTranslationComponent extends FiComponentsContainer implements OnInit, OnDestroy {
    @Input() names: IContentLanguageLabelModel[];
    @Input() descriptions: IContentLanguageLabelModel[];
    @Input() descriptionLabel: string;
    @Input() languageVersionTextId: string;
    @Input() isSystemLanguage: boolean;
    @Input() defaultContentLanguageId: number;
    @Input() isLanguageSelectHiddenInMobile: boolean;
    
    public namesChange = output<IContentLanguageLabelModel[]>();
    public descriptionsChange = output<IContentLanguageLabelModel[]>();

    ccmSideList: SideListModel;
    ActiveItem: number;

    get NamePlaceholder(): string {
        return this.findDefaultLanguageValue(this.names);
    }

    get DescriptionPlaceholder(): string {
        return this.findDefaultLanguageValue(this.descriptions);
    }

    constructor(
        private contentLanguagesService: ContentLanguagesService,
        private renderer: Renderer2,
        private elementRef: ElementRef,
        private userDataService: FiUserDataService,
        private appTextService: AppTextService,
        private detectDevicesService: DetectDevicesService,
        private cdr: ChangeDetectorRef,
        private fiBrowserEventsService: FiBrowserEventsService,
    ) {
        super();
        this.renderer.addClass(this.elementRef.nativeElement, 'fi-container');
        this.renderer.addClass(this.elementRef.nativeElement, 'fi-content');
    }

    ngOnInit() {
        this.setActiveLanguageItem();
        this.setCcmSideList();
        this.subscribeOnResize();
    }

    private setActiveLanguageItem() {
        if (isDefined(this.defaultContentLanguageId)) {
            this.ActiveItem = this.defaultContentLanguageId;
        } else {
            this.ActiveItem = this.defaultContentLanguageId = this.isSystemLanguage
                ? this.contentLanguagesService.getSelectedLanguageIdByApplicationLang(this.names)
                : this.contentLanguagesService.GetDefaultLanguageId();
        }
    }

    private setCcmSideList() {
        this.ccmSideList = new SideListModel(
            FiSideListType.Translations,
            this.userDataService.applicationLanguage,
            this.userDataService.userData.PreferredContentLanguage,
        );

        const languagesData = !this.isSystemLanguage
            ? this.contentLanguagesService.GetContentLanguages()
            : this.contentLanguagesService.GetApplicationLanguages();
        this.ccmSideList.Data = languagesData.sort(comparator((item) => item.SortIndex));
        this.ccmSideList.DefaultItem = this.defaultContentLanguageId;
    }

    private subscribeOnResize() {
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(
                debounce(() => interval(200)),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe(() => this.cdr.markForCheck());
    }

    getNamePlaceholder(): string {
        return this.isNameRequired() ? this.appTextService.get('common.required') : this.NamePlaceholder;
    }

    getDescriptionPlaceholder(description: IContentLanguageLabelModel): string {
        return description.ContentLanguageId === this.defaultContentLanguageId ? '' : this.DescriptionPlaceholder;
    }

    // onNameChange(contentName, newValue: string) {
    //     if (contentName.ContentLanguageId == this.defaultContentLanguageId) this.nameChange.emit(newValue);
    // }

    findDefaultLanguageValue(itemsArray: IContentLanguageLabelModel[]): string {
        const itemsMap = createMap(itemsArray, 'ContentLanguageId');
        return itemsMap[this.defaultContentLanguageId]?.Value;
    }

    onShowNameError(showError: boolean, contentLanguageId: number) {
        const sideListItem: IContentLanguage = this.ccmSideList.Data.find((dataItem: IContentLanguage) => {
            return dataItem.Id == contentLanguageId;
        });
        if (sideListItem) sideListItem.$$showError = showError;
    }

    isNameRequired(): boolean {
        return !this.names.some((name: IContentLanguageLabelModel) => name.Value);
    }

    isInputVisible(name: IContentLanguageLabelModel): boolean {
        return name.ContentLanguageId === this.ActiveItem;
    }

    isSideListShown(): boolean {
        return this.isLanguageSelectHiddenInMobile ? !this.detectDevicesService.isMobilePhone() : true;
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
